<template>
  <DashboardLayout>
    <Head title="Security" />

    <DCard class="mb-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div>
          <h5 class="mb-1">Two-factor authentication</h5>
          <p class="text-muted small mb-0">
            This dashboard controls every hosted editor, so a stolen password
            should not be enough on its own. Codes come from an authenticator
            app (1Password, Authy, Google Authenticator).
          </p>
        </div>
        <DBadge :variant="enabled ? 'success' : 'secondary'" class="flex-shrink-0">
          {{ enabled ? "On" : "Off" }}
        </DBadge>
      </div>

      <DAlert v-if="errorMessage" variant="danger" class="mt-3 mb-0">
        {{ errorMessage }}
      </DAlert>

      <!-- Not enrolled -->
      <div v-if="!enabled && !pending" class="mt-3">
        <DButton variant="primary" :disabled="busy" @click="enable">
          <DSpinner v-if="busy" small class="me-2" />
          Enable two-factor authentication
        </DButton>
      </div>

      <!-- Enrolling: scan, SAVE THE RECOVERY CODES, then prove a code works.
           Recovery codes are shown HERE, before activation, and gated behind an
           explicit acknowledgement — because once 2FA is on, losing the
           authenticator with no saved codes locks the only admin out for good
           (there is no password-reset flow). -->
      <div v-if="pending" class="mt-4">
        <p class="small mb-3">
          Finish in three steps.
          <strong>2FA is not on until you confirm a code</strong> — a mis-scanned
          code can't lock you out.
        </p>

        <!-- Loading / failed states always keep an escape hatch. Without this, a
             failed enrolment GET (or a dismissed 423 password prompt) left a
             permanent spinner with no way back. -->
        <div v-if="!enrolmentLoaded" class="my-3">
          <div v-if="enrolmentError" class="text-danger small mb-2">
            Couldn't load the setup details. Try again, or cancel.
          </div>
          <DSpinner v-else class="mb-3 d-block" />
          <div class="d-flex gap-2">
            <DButton
              v-if="enrolmentError"
              variant="primary"
              :disabled="busy"
              @click="retryEnrolment"
            >
              Retry
            </DButton>
            <DButton variant="outline-secondary" :disabled="busy" @click="disable">
              Cancel
            </DButton>
          </div>
        </div>

        <div v-else class="d-flex flex-wrap gap-4 align-items-start">
          <!-- Fortify renders this SVG server-side from the freshly generated
               secret (bacon/bacon-qr-code). No user input reaches it, so v-html
               is not an injection surface here. -->
          <div v-if="qrSvg" class="qr-code border rounded p-2 bg-white" v-html="qrSvg" />

          <div class="flex-grow-1" style="min-width: 260px">
            <p class="fw-semibold small mb-1">1. Scan the code</p>
            <label class="form-label small text-muted mb-1">
              Or enter this key manually
            </label>
            <code class="d-block mb-3 p-2 bg-light rounded small text-break">
              {{ secretKey }}
            </code>

            <p class="fw-semibold small mb-1">2. Save your recovery codes</p>
            <p class="text-muted small mb-2">
              Each logs you in once if you lose your authenticator. Store them
              somewhere safe now — this is the only time they're shown during
              setup.
            </p>
            <div class="bg-light rounded p-3 mb-2">
              <code
                v-for="code in recoveryCodes"
                :key="code"
                class="d-block small py-1"
              >{{ code }}</code>
            </div>
            <div class="form-check mb-3">
              <input
                id="ack-codes"
                v-model="codesAcknowledged"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label small" for="ack-codes">
                I've saved my recovery codes somewhere safe
              </label>
            </div>

            <p class="fw-semibold small mb-1">3. Confirm a code</p>
            <label class="form-label small" for="confirm-code">
              Code from your app
            </label>
            <DFormInput
              id="confirm-code"
              v-model="confirmCode"
              placeholder="123456"
              autocomplete="one-time-code"
              inputmode="numeric"
              :state="codeError ? false : null"
              class="mb-2"
              @keyup.enter="confirm"
            />
            <div v-if="codeError" class="text-danger small mb-2">
              {{ codeError }}
            </div>

            <div class="d-flex gap-2">
              <DButton variant="primary" :disabled="busy || !confirmReady" @click="confirm">
                <DSpinner v-if="busy" small class="me-2" />
                Confirm and turn on
              </DButton>
              <DButton variant="outline-secondary" :disabled="busy" @click="disable">
                Cancel
              </DButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Enabled -->
      <div v-if="enabled" class="mt-3">
        <DAlert v-if="regenerateWarning" variant="warning" class="mb-3">
          {{ regenerateWarning }}
        </DAlert>
        <div class="d-flex gap-2">
          <DButton variant="outline-secondary" :disabled="busy" @click="showRecoveryCodes">
            Show recovery codes
          </DButton>
          <DButton variant="outline-danger" :disabled="busy" @click="disable">
            Turn off
          </DButton>
        </div>
      </div>
    </DCard>

    <!-- Recovery codes (post-enrolment view) -->
    <DCard v-if="enabled && recoveryCodes.length > 0">
      <h5 class="mb-1">Recovery codes</h5>
      <p class="text-muted small">
        Store these somewhere safe. Each one logs you in once if you lose your
        authenticator — without them, losing your device means losing access to
        the control plane.
      </p>
      <div class="bg-light rounded p-3 mb-3">
        <code
          v-for="code in recoveryCodes"
          :key="code"
          class="d-block small py-1"
        >{{ code }}</code>
      </div>
      <DButton variant="outline-secondary" size="sm" :disabled="busy" @click="regenerate">
        Regenerate codes
      </DButton>
    </DCard>

    <!-- Password confirmation. Fortify guards every 2FA management route with
         `password.confirm`, which answers our JSON requests with 423 rather than
         redirecting. We catch that, prompt here, then replay the original
         action — so a hijacked session can't silently strip 2FA back off. -->
    <DModal
      v-model="passwordModalOpen"
      title="Confirm your password"
      :hide-footer="true"
    >
      <p class="text-muted small">
        This is a secure area. Confirm your password to continue.
      </p>
      <DFormInput
        v-model="password"
        type="password"
        placeholder="Password"
        autocomplete="current-password"
        :state="passwordError ? false : null"
        class="mb-2"
        @keyup.enter="submitPassword"
      />
      <div v-if="passwordError" class="text-danger small mb-2">
        {{ passwordError }}
      </div>
      <div class="d-flex justify-content-end gap-2 mt-3">
        <DButton variant="outline-secondary" @click="passwordModalOpen = false">
          Cancel
        </DButton>
        <DButton variant="primary" :disabled="busy || !password" @click="submitPassword">
          <DSpinner v-if="busy" small class="me-2" />
          Confirm
        </DButton>
      </div>
    </DModal>
  </DashboardLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { Head } from "@inertiajs/vue3";
import {
  api,
  type ApiError,
  DCard,
  DButton,
  DBadge,
  DAlert,
  DModal,
  DSpinner,
  DFormInput,
} from "@omnitend/dashboard-for-laravel";
import DashboardLayout from "../Layouts/DashboardLayout.vue";

const props = defineProps<{
  twoFactorEnabled: boolean;
  twoFactorPending: boolean;
}>();

const enabled = ref(props.twoFactorEnabled);
const pending = ref(props.twoFactorPending);

const qrSvg = ref<string | null>(null);
const secretKey = ref<string | null>(null);
const recoveryCodes = ref<string[]>([]);
const enrolmentLoaded = ref(false);
const enrolmentError = ref(false);
const confirmCode = ref("");
const codesAcknowledged = ref(false);
const regenerateWarning = ref("");

const busy = ref(false);
const errorMessage = ref("");
const codeError = ref("");

const passwordModalOpen = ref(false);
const password = ref("");
const passwordError = ref("");

// The confirm PREREQUISITES — deliberately NOT including `busy`. `confirm()` runs
// inside run(), which sets busy=true first, so a guard that included !busy would
// be false by the time the action checks it and the request would never fire
// (that exact bug shipped once). The button's disabled binding adds `busy`
// separately; confirm() checks busy separately before calling run().
const confirmReady = computed(
  () =>
    enrolmentLoaded.value &&
    recoveryCodes.value.length > 0 &&
    codesAcknowledged.value &&
    confirmCode.value.length > 0,
);

const isApiError = (error: unknown): error is ApiError =>
  typeof error === "object" && error !== null && "status" in error;

// The action parked when a 423 asks for password re-confirmation, plus a
// generation counter. Every open bumps the generation; every dismissal (Cancel,
// Escape, backdrop, close-X — all funnel through the passwordModalOpen watcher)
// invalidates it. submitPassword captures both up front and refuses to run the
// action if either changed while its request was in flight. This is what closes
// the dismiss-during-await race across ALL close paths, and the
// second-423-while-first-submit-in-flight case.
let deferredAction: (() => Promise<void>) | null = null;
let confirmGeneration = 0;

const invalidateConfirmation = () => {
  deferredAction = null;
  confirmGeneration += 1;
  password.value = "";
  passwordError.value = "";
};

// Fires for EVERY close cause (button, Esc, backdrop, X, and our own
// programmatic close after a successful confirm — harmless there, the action is
// already captured + consumed).
watch(passwordModalOpen, (open) => {
  if (!open) {
    invalidateConfirmation();
  }
});

/**
 * Run a 2FA management action, transparently handling the password-confirmation
 * challenge. Fortify's `password.confirm` middleware answers a JSON request with
 * 423 Locked when the password hasn't been confirmed recently; we park the
 * action, prompt, and replay it once confirmed.
 */
const guarded = async (action: () => Promise<void>): Promise<void> => {
  errorMessage.value = "";
  try {
    await action();
  } catch (error) {
    if (isApiError(error) && error.status === 423) {
      deferredAction = action;
      confirmGeneration += 1;
      passwordError.value = "";
      passwordModalOpen.value = true;
      return;
    }
    throw error;
  }
};

/** Wrap a guarded action with the busy flag + a generic error surface. */
const run = async (action: () => Promise<void>): Promise<void> => {
  busy.value = true;
  try {
    await guarded(action);
  } catch (error) {
    errorMessage.value = isApiError(error)
      ? error.message
      : "Something went wrong. Please try again.";
  } finally {
    busy.value = false;
  }
};

// Fetch everything the enrolment screen needs IN ONE GO, while the freshly
// confirmed password window is open: QR, manual key, AND recovery codes. The
// codes exist the moment 2FA is enabled (before it's confirmed), so showing them
// now — rather than after activation — means there is no window where 2FA is on
// but the codes were never displayed. `enrolmentLoaded` gates the confirm step
// so a half-loaded screen (e.g. a resumed enrolment where a GET failed) can't be
// confirmed against blank codes.
const loadEnrolment = async () => {
  enrolmentLoaded.value = false;
  enrolmentError.value = false;
  try {
    const [qr, secret, codes] = await Promise.all([
      api.get<{ svg: string; url: string }>("/user/two-factor-qr-code"),
      api.get<{ secretKey: string }>("/user/two-factor-secret-key"),
      api.get<string[]>("/user/two-factor-recovery-codes"),
    ]);
    qrSvg.value = qr.data.svg;
    secretKey.value = secret.data.secretKey;
    recoveryCodes.value = codes.data;
    enrolmentLoaded.value = true;
  } catch (error) {
    // Surface a Retry/Cancel state rather than a permanent spinner. Re-throw so
    // run() also records the generic error and, for a 423, opens the password
    // prompt (dismissing which leaves us here, with the escape hatch shown).
    enrolmentError.value = true;
    throw error;
  }
};

const retryEnrolment = () => run(loadEnrolment);

const loadRecoveryCodes = async () => {
  const { data } = await api.get<string[]>("/user/two-factor-recovery-codes");
  recoveryCodes.value = data;
  regenerateWarning.value = "";
};

// Resume a pending enrolment on a fresh page load (refresh mid-setup). Without
// this the screen shows the pending UI with a blank QR/secret/codes; loading
// them here (and gating confirm on enrolmentLoaded) stops an admin activating
// 2FA against codes they never saw.
onMounted(() => {
  if (pending.value) {
    run(loadEnrolment);
  }
});

const enable = () =>
  run(async () => {
    codesAcknowledged.value = false;
    await api.post("/user/two-factor-authentication");
    pending.value = true;
    await loadEnrolment();
  });

const confirm = () => {
  // Gate BEFORE run() — run() sets busy=true, and the Enter handler bypasses the
  // button's disabled binding, so this is the real guard against confirming
  // against unloaded or unacknowledged codes.
  if (busy.value || !confirmReady.value) {
    return;
  }
  run(async () => {
    codeError.value = "";
    try {
      await api.post("/user/confirmed-two-factor-authentication", {
        code: confirmCode.value,
      });
    } catch (error) {
      // A wrong code is a 422 on `code` — keep the user on the enrolment step
      // with an inline error rather than blowing up the whole page.
      if (isApiError(error) && error.status === 422) {
        codeError.value =
          error.errors?.code?.[0] ?? "That code wasn't right. Try again.";
        return;
      }
      throw error;
    }

    // Recovery codes are already loaded + acknowledged from the enrolment step;
    // no post-activation fetch that could fail and leave 2FA on without codes.
    enabled.value = true;
    pending.value = false;
    qrSvg.value = null;
    secretKey.value = null;
    confirmCode.value = "";
    codesAcknowledged.value = false;
  });
};

const showRecoveryCodes = () => run(loadRecoveryCodes);

const regenerate = () =>
  run(async () => {
    regenerateWarning.value = "";
    try {
      await api.post("/user/two-factor-recovery-codes");
    } catch (error) {
      // The POST outcome is unknown — Fortify may already have invalidated the
      // old codes before the response failed. Never keep showing the old codes
      // as if they still work; force a re-fetch.
      recoveryCodes.value = [];
      regenerateWarning.value =
        "Couldn't confirm the codes were regenerated. Click \"Show recovery codes\" to load the current set before relying on your saved ones.";
      throw error;
    }

    // POST succeeded → the old codes are definitely invalid. Drop them before
    // fetching, so a failed GET can't leave stale codes on screen.
    recoveryCodes.value = [];
    try {
      await loadRecoveryCodes();
    } catch {
      regenerateWarning.value =
        "Your old recovery codes are no longer valid, but the new ones didn't load. Click \"Show recovery codes\" to fetch them before you leave this page.";
    }
  });

const disable = () =>
  run(async () => {
    await api.delete("/user/two-factor-authentication");
    enabled.value = false;
    pending.value = false;
    qrSvg.value = null;
    secretKey.value = null;
    confirmCode.value = "";
    codesAcknowledged.value = false;
    enrolmentLoaded.value = false;
    enrolmentError.value = false;
    recoveryCodes.value = [];
    regenerateWarning.value = "";
  });

const submitPassword = async () => {
  const action = deferredAction;
  const generation = confirmGeneration;

  if (action === null) {
    passwordModalOpen.value = false;
    return;
  }

  passwordError.value = "";
  busy.value = true;
  try {
    await api.post("/user/confirm-password", { password: password.value });
  } catch (error) {
    // Distinguish a rate limit from a wrong password — otherwise a 429 reads as
    // "wrong password" and the user keeps retrying into the throttle.
    if (isApiError(error) && error.status === 429) {
      passwordError.value =
        error.message || "Too many attempts. Wait a moment and try again.";
    } else {
      passwordError.value =
        (isApiError(error) ? error.errors?.password?.[0] : null) ??
        "That password wasn't right.";
    }
    return;
  } finally {
    busy.value = false;
  }

  // Dismissed (Cancel/Esc/backdrop) or superseded by a newer prompt while the
  // request was in flight → do NOT run the action, and do NOT close (a newer
  // modal may now be open for a different action).
  if (deferredAction !== action || confirmGeneration !== generation) {
    password.value = "";
    return;
  }

  // Consume before closing; the watcher's invalidateConfirmation then no-ops.
  deferredAction = null;
  confirmGeneration += 1;
  password.value = "";
  passwordModalOpen.value = false;
  await run(action);
};
</script>

<style scoped>
.qr-code :deep(svg) {
  display: block;
  width: 180px;
  height: 180px;
}
</style>
